/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { makeStyles } from '@material-ui/styles';
import React, { useEffect, useRef, useState } from 'react';
import { Publish } from '@material-ui/icons';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Divider, Typography } from '@material-ui/core';
import Button from '../common/Button';
import { AttachmentPreviewItem } from '../draft/attachments/uploader/preview/AttachmentPreviewItem';

const useStyles = makeStyles(theme => ({
  dropzone: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    borderRadius: '0.2rem',
    border: `0.1rem dashed ${theme.palette.input}`,
    position: 'relative',
    cursor: 'pointer',
    background: theme.palette.white,
    color: theme.palette.input,

    '&:hover': {
      background: theme.palette.keyboardFocusBackground,
      border: `0.1rem dashed ${theme.palette.dropzoneBorder}`,

      '& $uploadButton': {
        backgroundColor: `${theme.palette.white} !important`,
        color: `${theme.palette.mainText} !important`,
      },
      '& $dragDropIcon': {
        color: `${theme.palette.mainText} !important`,
      },
      '& $dragDropOrText': {
        color: `${theme.palette.mainText} !important`,
      },
      '& $dragDropText': {
        color: `${theme.palette.mainText} !important`,
      },
    },
  },
  dropzoneInner: {
    padding: '0.5rem 0.2rem',
    display: 'contents',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    position: 'relative',
    width: '100%',
    [theme.breakpoints.up('md')]: {
      display: 'flex',
    },
  },
  uploadButton: {
    margin: '1rem',
    backgroundColor: `${theme.palette.iconBackground} !important`,
    color: `${theme.palette.mainText} !important`,
    borderRadius: '0.2rem',
    fontSize: '0.9rem',

    '&:disabled': {
      color: `${theme.palette.secondary.active} !important`,
      backgroundColor: 'transparent !important',
    },
    '&:focus, &:active': {
      backgroundColor: `${theme.palette.iconBackground} !important`,
      color: `${theme.palette.mainText} !important`,
    },
  },
  uploadButtonText: {
    marginBottom: 0,
    fontWeight: 600,
  },
  disabledDropzone: {
    cursor: 'not-allowed',
    position: 'relative',
    background: theme.palette.elementBackground,
    color: theme.palette.secondary.active,
    borderRadius: '0.2rem',
    border: `0.1rem dashed ${theme.palette.secondary.active}`,

    '&:hover': {
      background: theme.palette.elementBackground,
      border: `0.1rem dashed ${theme.palette.secondary.active}`,

      '& $uploadButton': {
        backgroundColor: 'transparent !important',
        color: `${theme.palette.secondary.active} !important`,
      },
      '& $dragDropIcon': {
        color: `${theme.palette.secondary.active} !important`,
      },
      '& $dragDropOrText': {
        color: `${theme.palette.secondary.active} !important`,
      },
      '& $dragDropText': {
        color: `${theme.palette.secondary.active} !important`,
      },
    },
  },
  fileInput: {
    visibility: 'hidden',
    position: 'absolute',
    top: '0',
    bottom: '0',
    left: '0',
    right: '0',
    width: '100%',
  },
  preview: {
    color: theme.palette.black,
  },
  dragDrop: {
    display: 'flex',
    padding: '0 2rem 1rem 2rem',
    [theme.breakpoints.up('md')]: {
      padding: 0,
    },
  },
  dragDropOrText: {
    display: 'none',
    marginBottom: 0,
    [theme.breakpoints.up('md')]: {
      display: 'contents',
    },
  },
  dragDropIcon: {
    margin: 0,
    transform: 'rotate(180deg)',
    [theme.breakpoints.up('md')]: {
      margin: '0 0.6rem',
    },
  },
  dragDropText: {
    marginBottom: 0,
    textAlign: 'center',
  },
}));

const DropZone = ({
  id,
  customClasses,
  filesLimit,
  multipleFiles,
  maxFileSize,
  acceptedMimeTypes,
  showPreviews,
  onDeletePreviewFile,
  readOnly,
  item,
  onDrop,
  onDropRejected,
  disabled,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [currentFiles, setFiles] = useState([]);

  const inputEl = useRef(null);

  /**
   * If file is deleted from an external source, make sure to update the local state
   */
  useEffect(() => {
    if (!item || (Array.isArray(item) && item.length === 0)) {
      inputEl.current.value = null;
      setFiles(item);
    }
  }, [JSON.stringify(item)]);

  /**
   * Prevent default event. Just in case
   * @param {Object} event
   */
  const stopEvent = (evt) => {
    evt.preventDefault();
    evt.stopPropagation();
  };

  /**
   * Validate uploaded file against accepted type, size, files limit and duplicate name
   * @param {Array} files
   */
  const validateFiles = (files) => {
    if (files?.length === 0) {
      return false;
    }
    const { type, size, name } = files[0];

    if (maxFileSize && size > maxFileSize) return false;
    if (currentFiles?.length > 0 && currentFiles.find(file => file.name === name)) return false;

    if (!multipleFiles && files?.length > 1) return false;
    if (multipleFiles && files?.length > filesLimit) return false;

    if (type && acceptedMimeTypes?.includes(type)) return true;

    const extension = name.split('.')[1] || '';
    for (const acceptedType of acceptedMimeTypes) {
      if (extension.toUpperCase() === acceptedType.replace('.', '').toUpperCase()) return true;
    }

    return false;
  };

  /**
   * Converts FileList into Array
   *  @param {Array} list
   */
  const fileListToArray = (list) => {
    let result;
    if (filesLimit > 1 || multipleFiles) {
      result = currentFiles?.length > 0 ? currentFiles : [];
    } else {
      result = [];
    }

    for (const file of list) {
      result.push(file);
    }

    return result;
  };

  /**
   * Handle file dropped into drag area or adding files through file dialog
   * @param {Object} event
   */

  const onFilesAdded = (evt) => {
    stopEvent(evt);
    const { files } = evt.dataTransfer || evt.target;

    if (files && validateFiles(files)) {
      if (onDrop(files)) {
        setFiles(fileListToArray(files));
      }
    } else {
      onDropRejected(files);
    }

    // Make sure to clean the input in order to be able to upload other files
    // (the current files are kept in the local state)
    inputEl.current.value = null;
  };

  /**
   * Opens file system dialog
   */
  const openFileDialog = () => {
    inputEl.current.click();
  };

  /**
   * Remove uploaded file by filename
   * @param {String} fileName
   */
  const deletePreviewFile = (fileName) => {
    if (filesLimit === 1) setFiles([]);
    if (filesLimit > 1 || multipleFiles) {
      const deletedFileIndex = currentFiles.findIndex(file => file.name === fileName);
      const modifiedFilesArray = currentFiles;
      modifiedFilesArray.splice(deletedFileIndex, 1);
      setFiles(modifiedFilesArray);
    }

    inputEl.current.value = null;
    onDeletePreviewFile(fileName);
  };

  return (
    <div
      id={id}
      draggable
      className={clsx(classes.dropzone, disabled ? classes.disabledDropzone : null, customClasses || null)}
      onDrop={(evt) => {
        if (!disabled) onFilesAdded(evt);
      }}
      onDragOver={evt => stopEvent(evt)}
      onDragLeave={evt => stopEvent(evt)}
    >
      <div id={`${id}-file-area`} className={classes.dropzoneInner}>
        <Button
          id={`${id}-upload-file-button`}
          onClick={openFileDialog}
          className={classes.uploadButton}
          disabled={disabled}
        >
          <Typography className={classes.uploadButtonText} variant="body1" component="p" gutterBottom>
            {t('attachments.upload.file')}
          </Typography>
        </Button>
        <div id={`${id}-drop-file-area`} className={classes.dragDrop}>
          <Typography className={classes.dragDropOrText} variant="body1" component="p" gutterBottom>
            {t('attachments.or')}
          </Typography>
          <Publish className={classes.dragDropIcon} />
          <Typography className={classes.dragDropText} variant="body1" component="p" gutterBottom>
            {t('attachments.drop.files')}
          </Typography>
        </div>
        <input
          id={`${id}-input`}
          data-testid="dropzoneInput"
          ref={inputEl}
          type="file"
          accept={acceptedMimeTypes}
          className={classes.fileInput}
          onChange={(evt) => {
            if (!disabled) onFilesAdded(evt);
          }}
          disabled={disabled}
        />
      </div>

      {showPreviews &&
        currentFiles?.length > 0 &&
        currentFiles?.map((file) => {
          return (
            <div key={file?.name}>
              <Divider variant="middle" />
              <div id={file?.name} className={classes.preview}>
                <AttachmentPreviewItem
                  originalFileName={file?.path}
                  onDeletePreviewFile={() => deletePreviewFile(file?.name)}
                  item={file}
                  readOnly={readOnly}
                />
              </div>
            </div>
          );
        })}
    </div>
  );
};

export default DropZone;
